import mitt from 'mitt';

/**
 * Event Hub using MITT library with Vue-compatible API.
 * Provides global event bus for component communication.
 * Maintains identical API to previous Vue instance implementation for backward compatibility.
 */
class EventHub {
	private emitter = mitt();

	// Vue-compatible event methods to maintain API compatibility

	/**
	 * Register an event listener.
	 * @description Supports both single event names and arrays of event names.
	 * @param eventName - Event name(s) to listen for
	 * @param handler - Callback function to execute when event is emitted
	 */
	$on(eventName: string | string[], handler: (...args: any[]) => void): void {
		if (Array.isArray(eventName)) {
			eventName.forEach((name) => {
				this.emitter.on(name, handler);
			});
		} else {
			this.emitter.on(eventName, handler);
		}
	}

	/**
	 * Unregister an event listener.
	 * @description If no eventName provided, clears all listeners. Supports single names and arrays.
	 * @param eventName - Event name(s) to stop listening for (optional)
	 * @param handler - Specific handler to remove (optional, removes all if not provided)
	 */
	$off(eventName?: string | string[], handler?: (...args: any[]) => void): void {
		if (!eventName) {
			// Clear all listeners
			this.emitter.all.clear();
			return;
		}

		if (Array.isArray(eventName)) {
			eventName.forEach((name) => {
				if (handler) {
					this.emitter.off(name, handler);
				} else {
					this.emitter.off(name);
				}
			});
		} else if (handler) {
			this.emitter.off(eventName, handler);
		} else {
			this.emitter.off(eventName);
		}
	}

	/**
	 * Emit an event to all registered listeners.
	 * @description Dispatches event with provided arguments to all registered handlers.
	 * @param eventName - Event name to emit
	 * @param args - Arguments to pass to event handlers
	 */
	$emit(eventName: string, ...args: any[]): void {
		this.emitter.emit(eventName, args.length === 1 ? args[0] : args);
	}

	/**
	 * Register a one-time event listener.
	 * @description Listener automatically unregisters after first event emission.
	 * @param eventName - Event name to listen for
	 * @param handler - Callback function to execute once when event is emitted
	 */
	$once(eventName: string, handler: (...args: any[]) => void): void {
		const onceHandler = (data: any) => {
			handler(data);
			this.emitter.off(eventName, onceHandler);
		};
		this.emitter.on(eventName, onceHandler);
	}
}

/**
 * Global event hub singleton instance.
 * Use this for emitting and listening to application-wide events.
 */
export const eventHub = new EventHub();
